import { forwardRef, MutableRefObject, useLayoutEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import Button from '@/components/Button/Button'
import UIText from '@/components/UI/UIText'
import { useGSAP } from '@/composables/useGSAP'

export type QuestionsExplanationsItemProps = {
  className?: string
  question: string
  answer: string
  goodAnswer: string
  explanation: string

  isLeaving: boolean
}

const QuestionsBinaryExplanationsItem = forwardRef<HTMLDivElement, QuestionsExplanationsItemProps>(
  ({ isLeaving, className, question, answer, goodAnswer, explanation }, ref) => {
    const { t } = useTranslation()
    const { gsap } = useGSAP()

    useLayoutEffect(() => {
      const ctx = gsap.context(() => {
        gsap.timeline().fromTo(
          (ref as MutableRefObject<HTMLDivElement>).current,
          {
            opacity: isLeaving ? 1 : 0,
          },
          {
            opacity: isLeaving ? 0 : 1,
            duration: isLeaving ? 0.3 : 0.5,
            ease: 'power2.out',
            // stagger: {
            //   amount: 0.2,
            //   from: isLeaving ? 'end' : 'start'
            // }
          },
          0
        )
      })

      return () => ctx.kill()
    }, [gsap, ref, isLeaving])

    const hasRightAnswer = useMemo(() => {
      return answer === goodAnswer
    }, [answer, goodAnswer])

    return (
      <div
        ref={ref}
        className='QuestionBinaryExplanationsItem relative my-auto z-2 w-full portrait:max-w-cols-9 md:max-w-cols-4 flex flex-col items-center justify-center gap-24'
      >
        <UIText is='div' className='text-20 md:text-24 text-center font-semibold' i18nKey={question} />

        <div className='flex items-center justify-center gap-12'>
          <Button
            className='w-[160px]'
            appear={true}
            appearDelay={0.2}
            variant='question'
            questionState={hasRightAnswer ? 'correct' : 'incorrect'}
          >
            {answer === 'TRUE' ? t(`questions.true`) : <span className='text-dark-blue'>{t(`questions.false`)}</span>}
          </Button>

          {!hasRightAnswer && (
            <Button
              className='w-[160px] opacity-30'
              appear={true}
              appearDelay={0.5}
              variant='question'
              questionState={!hasRightAnswer ? 'correct' : 'incorrect'}
            >
              {goodAnswer === 'TRUE' ? (
                t(`questions.true`)
              ) : (
                <span className='text-dark-blue'>{t(`questions.false`)}</span>
              )}
            </Button>
          )}
        </div>

        <UIText is='p' delay={0.65} className='text-body text-left' i18nKey={explanation} />
      </div>
    )
  }
)

export default QuestionsBinaryExplanationsItem
